/**
 * @name FormDistribuirSaldo
 * @author Gabriel M. Guimarães | gabrielmg7
 * @description Modal/formulário para distribuir saldo para dependentes
 * @package components/pages/gestao-financeira/dependentes/components
 */

import { AttachMoney as AttachMoneyIcon, Person as PersonIcon } from '@mui/icons-material';
import { Alert, Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, FormControl, InputAdornment, InputLabel, MenuItem, Select, TextField, Typography } from '@mui/material';
import React, { useState } from 'react';
import { useAlert } from '../../../../../contexts';
import { currencyFormatterParser } from '../../../../../utils/handleChange';

interface FormDistribuirSaldoProps {
     /**
      * Controla se o modal está aberto
      */
     open: boolean;

     /**
      * Função chamada ao fechar o modal
      */
     onClose: () => void;

     /**
      * Lista de dependentes disponíveis
      */
     dependentes: Array<{
          id_aluno: number | null;
          aluno: string | null;
          saldo_disponivel: number | null;
     }>;

     /**
      * ID do dependente pré-selecionado (opcional)
      */
     dependenteSelecionado?: number | null;

     /**
      * Função chamada ao confirmar a distribuição
      */
     onDistribuir: (dados: { idAluno: number; valor: number; observacoes?: string }) => Promise<boolean>;
}

/**
 * Modal para distribuir saldo para dependentes
 */
const FormDistribuirSaldo: React.FC<FormDistribuirSaldoProps> = ({ open, onClose, dependentes, dependenteSelecionado, onDistribuir }) => {
     const { showError } = useAlert();

     // Estados do formulário
     const [idAluno, setIdAluno] = useState<number>(dependenteSelecionado || 0);
     const [valor, setValor] = useState<string>('');
     const [valorNumerico, setValorNumerico] = useState<number>(0);
     const [observacoes, setObservacoes] = useState<string>('');
     const [loading, setLoading] = useState(false);

     // Limpa o formulário ao abrir/fechar
     React.useEffect(() => {
          if (open) {
               setIdAluno(dependenteSelecionado || 0);
               setValor('');
               setValorNumerico(0);
               setObservacoes('');
               setLoading(false);
          }
     }, [open, dependenteSelecionado]);

     // Validações
     const dependenteEscolhido = dependentes.find((d) => d.id_aluno === idAluno);
     const isFormValid = idAluno > 0 && valorNumerico > 0 && valorNumerico <= 500; // Limite máximo de R$ 500

     // Handler para distribuir saldo
     const handleDistribuir = async () => {
          if (!isFormValid) {
               showError('Preencha todos os campos corretamente');
               return;
          }

          setLoading(true);
          try {
               const sucesso = await onDistribuir({
                    idAluno,
                    valor: valorNumerico,
                    observacoes: observacoes.trim() || undefined,
               });

               if (sucesso) {
                    onClose();
               }
          } catch (error) {
               console.error('Erro ao distribuir saldo:', error);
          } finally {
               setLoading(false);
          }
     };

     return (
          <Dialog
               open={open}
               onClose={onClose}
               maxWidth='sm'
               fullWidth
               PaperProps={{
                    sx: { borderRadius: 2 },
               }}
          >
               <DialogTitle sx={{ pb: 1 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                         <AttachMoneyIcon color='primary' />
                         <Typography
                              variant='h6'
                              component='span'
                         >
                              Distribuir Saldo
                         </Typography>
                    </Box>
                    <Typography
                         variant='body2'
                         color='text.secondary'
                         sx={{ mt: 1 }}
                    >
                         Transfira créditos para o dependente selecionado
                    </Typography>
               </DialogTitle>

               <DialogContent sx={{ pt: 2 }}>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                         {/* Seleção do Dependente */}
                         <FormControl fullWidth>
                              <InputLabel>Dependente</InputLabel>
                              <Select
                                   value={idAluno}
                                   label='Dependente'
                                   onChange={(e) => setIdAluno(Number(e.target.value))}
                                   disabled={loading}
                                   startAdornment={
                                        <InputAdornment position='start'>
                                             <PersonIcon />
                                        </InputAdornment>
                                   }
                              >
                                   <MenuItem value={0}>
                                        <em>Selecione um dependente</em>
                                   </MenuItem>
                                   {dependentes.map((dependente) => (
                                        <MenuItem
                                             key={dependente.id_aluno}
                                             value={dependente.id_aluno || 0}
                                             disabled={!dependente.id_aluno}
                                        >
                                             <Box>
                                                  <Typography variant='body1'>{dependente.aluno || 'Nome não informado'}</Typography>
                                                  <Typography
                                                       variant='caption'
                                                       color='text.secondary'
                                                  >
                                                       Saldo atual: R$ {(dependente.saldo_disponivel || 0).toFixed(2)}
                                                  </Typography>
                                             </Box>
                                        </MenuItem>
                                   ))}
                              </Select>
                         </FormControl>

                         {/* Valor a Distribuir */}
                         <TextField
                              label='Valor a Distribuir'
                              type='text'
                              value={valor}
                              onChange={(e) => {
                                   const { formatted, numeric } = currencyFormatterParser(e.target.value);
                                   setValor(formatted);
                                   setValorNumerico(numeric);
                              }}
                              disabled={loading}
                              fullWidth
                              InputProps={{
                                   startAdornment: <InputAdornment position='start'>R$</InputAdornment>,
                                   inputMode: 'numeric',
                              }}
                              helperText={`Limite máximo: R$ 500,00 ${valorNumerico > 500 ? '⚠️' : ''}`}
                              error={valorNumerico > 500}
                         />

                         {/* Observações */}
                         <TextField
                              label='Observações (opcional)'
                              multiline
                              rows={3}
                              value={observacoes}
                              onChange={(e) => setObservacoes(e.target.value)}
                              disabled={loading}
                              fullWidth
                              placeholder='Ex: Dinheiro para merenda da semana'
                         />

                         {/* Informações do Dependente Selecionado */}
                         {dependenteEscolhido && (
                              <Alert
                                   severity='info'
                                   sx={{ mt: 1 }}
                              >
                                   <Typography variant='body2'>
                                        <strong>{dependenteEscolhido.aluno}</strong> receberá {valor}
                                        <br />
                                        Saldo atual: R$ {(dependenteEscolhido.saldo_disponivel || 0).toFixed(2)}
                                        <br />
                                        Saldo após transferência: R$ {((dependenteEscolhido.saldo_disponivel || 0) + valorNumerico).toFixed(2)}
                                   </Typography>
                              </Alert>
                         )}

                         {/* Validações */}
                         {valorNumerico > 0 && valorNumerico > 500 && <Alert severity='error'>O valor máximo para distribuição é R$ 500,00</Alert>}
                    </Box>
               </DialogContent>

               <DialogActions sx={{ p: 3, pt: 1 }}>
                    <Button
                         onClick={onClose}
                         disabled={loading}
                         color='inherit'
                    >
                         Cancelar
                    </Button>
                    <Button
                         onClick={handleDistribuir}
                         variant='contained'
                         disabled={!isFormValid || loading}
                         startIcon={<AttachMoneyIcon />}
                    >
                         {loading ? 'Distribuindo...' : `Distribuir ${valor}`}
                    </Button>
               </DialogActions>
          </Dialog>
     );
};

export default FormDistribuirSaldo;
